'use client';

import { useState, useEffect, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../utils/cn';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Server, Globe, Shield } from 'lucide-react';
import './Header.css';

const navItems = [
  { name: 'Home', href: '/' },
  { 
    name: 'Hosting', 
    href: '/hosting',
    submenu: [
      { name: 'Shared Hosting', href: '/hosting/shared-hosting' },
      { name: 'WordPress Hosting', href: '/hosting/wordpress-hosting' },
      { name: 'VPS Hosting', href: '/hosting/vps-hosting' },
      { name: 'Dedicated Server', href: '/hosting/dedicated-server' },
    ]
  },
  { 
    name: 'Domains', 
    href: '/domains'
  },
  { name: 'About Us', href: '/about' },
  { name: 'Blog', href: '/blog' },
  { name: 'Contact Us', href: '/contact' },
  
];

const MotionDiv = motion.div;

function HeaderContent() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const toggleSubmenu = (href: string) => {
    if (activeSubmenu === href) {
      setActiveSubmenu(null);
    } else {
      setActiveSubmenu(href);
    }
  };

  return (
    <header 
      className={cn(
        "fixed top-0 w-full z-50 transition-all duration-500",
        scrolled ? "header-scrolled" : "header-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <MotionDiv
              initial={{ y: -100 }}
              animate={{ y: 0 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
            >
              <Link to="/" className="flex-shrink-0 flex items-center">
                <MotionDiv
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="/images/logo-white.png"
                    alt="1 Dollar Cloud"
                    width={180}
                    height={40}
                    className="h-10 w-auto transition-all duration-300"
                    fetchPriority="high"
                    decoding="async"
                  />
                </MotionDiv>
              </Link>
            </MotionDiv>
          </div>
          
          {/* Desktop navigation */}
          <nav className="hidden md:ml-6 md:flex md:items-center md:space-x-2">
            {navItems.map((item, index) => (
              <div key={item.name} className="relative group flex items-center">
                <MotionDiv
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100
                  }}
                >
                  {item.external ? (
                    <a
                      href={item.href}
                      className={cn(
                        "inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-300",
                        "text-slate-200 hover:bg-primary-dark/10 hover:text-primary-light"
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        "inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-300",
                        pathname === item.href || (pathname ?? "")?.startsWith(`${item.href}/`)
                          ? "bg-primary-dark/20 text-primary-light shadow-sm"
                          : "text-slate-200 hover:bg-primary-dark/10 hover:text-primary-light"
                      )}
                      onClick={() => item.submenu && toggleSubmenu(item.href)}
                    >
                      {item.name}
                      {item.submenu && (
                        <ChevronDown className="ml-1.5 h-4 w-4 group-hover:rotate-180 transition-transform duration-300" />
                      )}
                    </Link>
                  )}
                </MotionDiv>
                
                {item.submenu && (
                  <div className="absolute left-0 top-full mt-1 w-56 origin-top-left rounded-xl shadow-lg overflow-hidden z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <div className="py-2 bg-slate-800 backdrop-blur-xl bg-opacity-90 border border-slate-700 rounded-xl shadow-3d">
                      {item.submenu.map((subitem) => (
                        <MotionDiv
                          key={subitem.name}
                          whileHover={{ x: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Link
                            to={subitem.href}
                            className={cn(
                              "block px-4 py-2 text-sm transition-colors duration-200",
                              pathname === subitem.href
                                ? "bg-primary-dark/20 text-primary-light"
                                : "text-slate-300 hover:bg-primary-dark/10 hover:text-primary-light"
                            )}
                          >
                            {subitem.name}
                          </Link>
                        </MotionDiv>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>
          
          {/* Login/Signup buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <MotionDiv
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              whileHover={{ y: -3 }}
            >
              <a
                href="https://panel.1dollarcloud.com/index.php?fuse=home&view=login"
                className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-full shadow-neon text-white bg-gradient-rainbow hover:shadow-neon-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300"
              >
                Login
              </a>
            </MotionDiv>
          </div>
          
          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-slate-300 hover:text-primary-light hover:bg-primary-dark/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary transition-colors duration-200"
            >
              <span className="sr-only">Open main menu</span>
              <MotionDiv whileTap={{ scale: 0.95 }}>
                {mobileMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </MotionDiv>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <MotionDiv
        initial={false}
        animate={
          mobileMenuOpen
            ? { height: 'auto', opacity: 1 }
            : { height: 0, opacity: 0 }
        }
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="md:hidden overflow-hidden"
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-slate-900/90 backdrop-blur-lg shadow-lg mt-1 rounded-lg mx-2">
          {navItems.map((item, index) => (
            <MotionDiv 
              key={item.name}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div
                className="flex items-center justify-between px-3 py-2 rounded-md text-base font-medium text-slate-200 hover:text-primary-light hover:bg-primary-dark/10"
                onClick={() => item.submenu && toggleSubmenu(item.href)}
              >
                {item.external ? (
                  <a
                    href={item.href}
                    className="flex items-center"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link to={item.href} className="flex items-center">
                    {item.name}
                  </Link>
                )}
                {item.submenu && (
                  <MotionDiv
                    animate={{ rotate: activeSubmenu === item.href ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="h-5 w-5" />
                  </MotionDiv>
                )}
              </div>
              
              {item.submenu && (
                <MotionDiv
                  initial={false}
                  animate={
                    activeSubmenu === item.href
                      ? { height: 'auto', opacity: 1, marginTop: 4 }
                      : { height: 0, opacity: 0, marginTop: 0 }
                  }
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="pl-4 pr-2 py-2 space-y-1 bg-slate-800 rounded-md">
                    {item.submenu.map((subitem, subIndex) => (
                      <MotionDiv
                        key={subitem.name}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: subIndex * 0.1 }}
                      >
                        <Link
                          to={subitem.href}
                          className={cn(
                            "block px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200",
                            pathname === subitem.href
                              ? "bg-primary-dark/20 text-primary-light"
                              : "text-slate-300 hover:bg-primary-dark/10 hover:text-primary-light"
                          )}
                          onClick={() => {
                            setActiveSubmenu(null);
                            setMobileMenuOpen(false);
                          }}
                        >
                          {subitem.name}
                        </Link>
                      </MotionDiv>
                    ))}
                  </div>
                </MotionDiv>
              )}
            </MotionDiv>
          ))}
          
          <div className="pt-4 pb-3 border-t border-slate-700">
            <MotionDiv
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <div className="flex items-center px-5">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-rainbow flex items-center justify-center text-white">
                    <span className="text-lg font-semibold">G</span>
                  </div>
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-slate-200">Guest User</div>
                  <div className="text-sm font-medium text-slate-400">guest@example.com</div>
                </div>
              </div>
            </MotionDiv>
            <div className="mt-3 px-2 space-y-1">
              <MotionDiv
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <a
                  href="https://panel.1dollarcloud.com/index.php?fuse=home&view=login"
                  className="block px-3 py-2 rounded-md text-base font-medium bg-gradient-rainbow text-white hover:opacity-90"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </a>
              </MotionDiv>
            </div>
          </div>
        </div>
      </MotionDiv>
    </header>
  );
}

export default function Header() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <HeaderContent />
    </Suspense>
  );
}